import React, { Suspense, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { isOnUnsupportedBrowser } from '../../global/browser';
import { hasFeatureInLocalStorage, verifyFeatureAccess } from '../../global/featureFlags';
import { loadFallbackViewer, loadNutrientViewer, loadPSPDFViewer } from './viewerComponentImports';

const FallbackViewer = React.lazy(loadFallbackViewer);
const PSPDFViewer = React.lazy(loadPSPDFViewer);
const NutrientViewer = React.lazy(loadNutrientViewer);

export const NUTRIENT_VIEWER_FLAG = 'nutrient_viewer';

export const ENGINE_NUTRIENT = 'nutrient';
export const ENGINE_PSPDFKIT = 'pspdfkit';
export const ENGINE_FALLBACK = 'fallback';

// Surface x mode stage table for the Nutrient migration's routing matrix
// (docs/plans/2026-06-12-001-refactor-nutrient-viewer-migration-plan.md).
// A cell is true once its cutover stage ships:
//   Stage A (U4)  SHIPPED: the readOnly cells on all five surfaces are live,
//   Stage B (U9)  SHIPPED: the sign cells (guest, link, patient) are live,
//   Stage C (U11) SHIPPED: the edit cells (newRecordViewer, recordViewer,
//                 iframe) are live.
// With every stage shipped, flagged orgs render the Nutrient viewer in every
// surface and mode; the table stays as the per-cell rollback switch (flip a
// single cell back to false to route just that surface/mode to the old
// viewer). Exported so tests can exercise cell rollbacks directly.
export const STAGE_CELLS = {
  newRecordViewer: { readOnly: true, edit: true },
  recordViewer: { readOnly: true, edit: true },
  guest: { readOnly: true, sign: true },
  link: { readOnly: true, sign: true },
  patient: { readOnly: true, sign: true },
  iframe: { edit: true },
};

// Mode predicates per surface, mirroring what each container passes today:
//   newRecordViewer / recordViewer: canEditRecord = canEditRecord && canEditAfterSend,
//     fromPage carries 'sent' for the sent inbox.
//   guest: canEditRecord = source !== 'api'.
//   link: canEditRecord is false for form_submission documents and api sources;
//     documentType is threaded explicitly so the form_submission predicate
//     never depends on how the container collapsed it into canEditRecord.
//   patient: canEditRecord = !disableSecureReplies.
//   iframe: never read-only (chrome extension editing surface).
const resolveMode = (surface, props) => {
  switch (surface) {
    case 'newRecordViewer':
    case 'recordViewer':
      return (props.fromPage === 'sent' || !props.canEditRecord) ? 'readOnly' : 'edit';
    case 'guest':
    case 'patient':
      return props.canEditRecord ? 'sign' : 'readOnly';
    case 'link':
      return (props.documentType === 'form_submission' || !props.canEditRecord) ? 'readOnly' : 'sign';
    case 'iframe':
      return 'edit';
    default:
      return null;
  }
};

// Pure routing decision: (viewer prop bag, org flag state) -> engine.
// The unsupported-browser check stays first: FallbackViewer wins regardless of
// flag state. Unknown surfaces (no viewerSurface threaded yet) always route to
// the old viewer. `stageCells` is parameterized for tests only; production
// callers use the module-level STAGE_CELLS.
export const routeEngine = (props, flagState, stageCells = STAGE_CELLS) => {
  if (isOnUnsupportedBrowser()) return ENGINE_FALLBACK;
  if (flagState !== true) return ENGINE_PSPDFKIT;
  const cells = stageCells[props.viewerSurface];
  const mode = resolveMode(props.viewerSurface, props);
  if (!cells || !mode) return ENGINE_PSPDFKIT;
  return cells[mode] === true ? ENGINE_NUTRIENT : ENGINE_PSPDFKIT;
};

const viewerLoadingFallback = (
  <div
    style={{
      alignItems: 'center', display: 'flex', height: '100%', justifyContent: 'center',
    }}
  >
    Loading viewer...
  </div>
);

const ViewerContainer = (props) => {
  const { nutrientViewerFlag, recordId } = props;

  // When an access payload delivers the org flag explicitly (unauthenticated
  // surfaces, delivered by the U7 backend work), the prop is authoritative:
  // localStorage and verifyFeatureAccess are never consulted, so a stale flag
  // cached from another org's login can never leak into routing (R24).
  const flagFromProp = typeof nutrientViewerFlag === 'boolean';

  // Result of the async flag refresh. Held in a ref (not render state) because
  // it must never re-route the currently pinned engine -- it only feeds the
  // NEXT pin evaluation, which always happens during a recordId-change render.
  const refreshedFlagRef = useRef(null);

  const resolveFlagState = () => {
    if (flagFromProp) return nutrientViewerFlag === true;
    if (refreshedFlagRef.current !== null) return refreshedFlagRef.current;
    return hasFeatureInLocalStorage(NUTRIENT_VIEWER_FLAG) === true;
  };

  // Engine pinned per record-mount-session (R23). The pin key is recordId --
  // never fileToken, because every save rotates the token in place and that
  // rotation must not re-pin (a mid-edit engine hot-swap would strand
  // unexported annotations and parent save callbacks). recordId change
  // re-evaluates and re-pins; unmount resets naturally.
  const pinRef = useRef(null);
  if (pinRef.current === null || pinRef.current.recordId !== recordId) {
    pinRef.current = { recordId, engine: routeEngine(props, resolveFlagState()) };
  }
  const { engine } = pinRef.current;

  // Refresh the flag cache once per surface mount (InboxRedesign precedent:
  // sync cache decides the current open, async refresh feeds the next). Skipped
  // entirely when the flag arrives by prop.
  useEffect(() => {
    if (flagFromProp) return undefined;
    let isMounted = true;
    (async () => {
      try {
        const active = await verifyFeatureAccess(NUTRIENT_VIEWER_FLAG);
        if (isMounted) refreshedFlagRef.current = active === true;
      } catch {
        // Old viewer is the safe default; verifyFeatureAccess only throws when
        // the cache is empty and the network fetch fails.
        if (isMounted) refreshedFlagRef.current = false;
      }
    })();
    return () => { isMounted = false; };
  }, [flagFromProp]);

  let Engine = PSPDFViewer;
  if (engine === ENGINE_FALLBACK) Engine = FallbackViewer;
  else if (engine === ENGINE_NUTRIENT) Engine = NutrientViewer;

  // The container's whole contract is forwarding each surface's prop bag to
  // the pinned engine untouched, so spreading is intentional here.
  return (
    <Suspense fallback={viewerLoadingFallback}>
      {/* eslint-disable-next-line react/jsx-props-no-spreading */}
      <Engine {...props} />
    </Suspense>
  );
};

ViewerContainer.propTypes = {
  recordId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  canEditRecord: PropTypes.bool,
  fromPage: PropTypes.string,
  documentType: PropTypes.string,
  viewerSurface: PropTypes.oneOf([
    'newRecordViewer',
    'recordViewer',
    'guest',
    'link',
    'patient',
    'iframe',
  ]),
  nutrientViewerFlag: PropTypes.bool,
};

// All defaults are intentionally undefined: every prop is forwarded to the
// pinned engine via spread, and a non-undefined default here would override
// the engines' own defaultProps.
ViewerContainer.defaultProps = {
  recordId: undefined,
  canEditRecord: undefined,
  fromPage: undefined,
  documentType: undefined,
  viewerSurface: undefined,
  nutrientViewerFlag: undefined,
};

export default ViewerContainer;
